import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { leadsAPI, notificationsAPI, billingAPI } from '../../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import MobileBottomNav from './MobileBottomNav';
import QuickAddLead from '../common/QuickAddLead';
import KeyboardShortcuts from '../common/KeyboardShortcuts';

const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const { isDark } = useTheme();
    const navigate = useNavigate();

    // State
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [todayLeadsCount, setTodayLeadsCount] = useState(0);
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [showShortcutHint, setShowShortcutHint] = useState(() => {
        return localStorage.getItem('offtrigger_shortcut_hint') !== 'hidden';
    });

    // Subscription / trial state
    const [subscriptionData, setSubscriptionData] = useState(null);

    // Fetch data function
    const fetchData = React.useCallback(async () => {
        try {
            const [statsRes, notifRes, billingRes] = await Promise.allSettled([
                leadsAPI.getStats(),
                notificationsAPI.getUnreadCount(),
                billingAPI.getSubscription(),
            ]);

            if (statsRes.status === 'fulfilled') {
                const todayCount = statsRes.value.data?.stats?.todayCount || statsRes.value.data?.today || 0;
                setTodayLeadsCount(todayCount);
            }

            if (notifRes.status === 'fulfilled') {
                setUnreadNotifications(notifRes.value.data?.count || 0);
            }

            if (billingRes.status === 'fulfilled' && billingRes.value?.data) {
                setSubscriptionData(billingRes.value.data);
            } else {
                // Fallback: detect expired state from 403 errors on other APIs
                const errors = [statsRes, notifRes].filter(r => r.status === 'rejected');
                const hasExpiredError = errors.some(r => {
                    const code = r.reason?.response?.data?.code;
                    return code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_REQUIRED';
                });
                if (hasExpiredError) {
                    setSubscriptionData({ status: 'expired', plan: 'free' });
                }
            }
        } catch (error) {
            console.error('Error fetching layout data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch data on mount
    useEffect(() => {
        if (user) {
            fetchData();
        } else {
            setLoading(false);
        }
    }, [user, fetchData]);

    const toggleSidebar = () => setSidebarOpen(prev => !prev);
    const closeSidebar = () => setSidebarOpen(false);
    const handleLogout = () => logout();

    // Determine trial/subscription state
    const isTrialStatus = subscriptionData?.status === 'trial' || subscriptionData?.status === 'trialing';
    const trialDaysLeft = subscriptionData?.trial_days_remaining;
    const trialEndsAt = subscriptionData?.trial_ends_at;

    // Trial is expiring soon (within 3 days)
    const isTrialExpiringSoon = isTrialStatus && trialDaysLeft !== null && trialDaysLeft <= 3 && trialDaysLeft > 0;

    // Trial has ended or no active subscription
    const isExpiredOrInactive = (
        (isTrialStatus && trialDaysLeft !== null && trialDaysLeft <= 0) ||
        subscriptionData?.status === 'expired' ||
        (subscriptionData?.status === 'inactive' && (!subscriptionData?.plan || subscriptionData?.plan === 'free'))
    );

    // Don't show expired banner on the billing page itself
    const isBillingPage = window.location.pathname === '/billing';

    const location = useLocation();
    const mainContentRef = React.useRef(null);

    // Scroll to top of main content on route change
    useEffect(() => {
        if (mainContentRef.current) {
            mainContentRef.current.scrollTo(0, 0);
        }
    }, [location.pathname]);

    if (loading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-navy' : 'bg-mint'}`}>
                <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? 'border-cyan' : 'border-primary'}`}></div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen font-sans antialiased overflow-hidden ${isDark ? 'bg-navy' : 'bg-mint'}`}>
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                    onClick={closeSidebar}
                />
            )}

            {/* Top Navbar */}
            <Navbar
                user={user}
                unreadCount={unreadNotifications}
                onMenuClick={toggleSidebar}
                onLogout={handleLogout}
                isTrialActive={isTrialStatus}
                trialExpiringSoon={isTrialExpiringSoon}
                trialDaysLeft={trialDaysLeft}
                trialEndsAt={trialEndsAt}
            />

            {/* Sidebar + Main Content */}
            <div className="flex h-screen pt-16">
                <Sidebar
                    user={user}
                    todayLeadsCount={todayLeadsCount}
                    unreadMessages={unreadNotifications}
                    isOpen={sidebarOpen}
                    onClose={closeSidebar}
                />

                {/* Main content area */}
                <main
                    ref={mainContentRef}
                    className={`flex-1 ml-0 lg:ml-64 overflow-y-auto p-4 md:p-6 lg:p-8 relative pb-20 lg:pb-8 ${isDark ? 'bg-navy' : 'bg-mint'}`}
                >
                    {/* Full-page expired / no subscription banner */}
                    {isExpiredOrInactive && !isBillingPage && (
                        <div className={`mb-6 rounded-xl border-2 p-6 flex flex-col sm:flex-row items-center gap-4 ${isDark
                            ? 'bg-red-500/10 border-red-500/30'
                            : 'bg-red-50 border-red-200'
                            }`}>
                            <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-red-500/20' : 'bg-red-100'
                                }`}>
                                <span className="material-symbols-outlined text-3xl text-red-500">warning</span>
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-light' : 'text-darkTeal'}`}>
                                    {isTrialStatus ? 'Your Free Trial Has Ended' : 'No Active Subscription'}
                                </h3>
                                <p className={`text-sm ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>
                                    Upgrade to a plan to continue using lead capture, auto-responses, and all OffTrigger features.
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/billing')}
                                className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold text-sm shadow-lg shadow-red-500/20 transition-all flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
                            >
                                <span className="material-symbols-outlined text-lg">rocket_launch</span>
                                Upgrade Now
                            </button>
                        </div>
                    )}
                    {children}
                </main>
            </div>

            {/* Footer Hint */}
            {showShortcutHint && (
                <footer className={`fixed bottom-20 lg:bottom-4 right-6 group transition-all duration-500 hidden md:block ${isDark ? 'text-light/20 hover:text-light/40' : 'text-darkTeal/20 hover:text-darkTeal/40'}`}>
                    <div className="flex items-center gap-2">
                        <p className="text-[10px] uppercase font-bold tracking-widest flex items-center gap-1.5">
                            <span className={`px-1 rounded border ${isDark ? 'border-white/10' : 'border-darkTeal/10'}`}>?</span>
                            Press ? for shortcuts
                        </p>
                        <button
                            onClick={() => {
                                setShowShortcutHint(false);
                                localStorage.setItem('offtrigger_shortcut_hint', 'hidden');
                            }}
                            className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto"
                            title="Dismiss"
                        >
                            <span className="material-symbols-outlined text-xs">close</span>
                        </button>
                    </div>
                </footer>
            )}

            {/* Mobile Bottom Navigation */}
            <MobileBottomNav
                onAddClick={() => setShowQuickAdd(true)}
                onMenuClick={toggleSidebar}
            />

            {/* Global Quick Add Lead Modal */}
            <QuickAddLead
                isOpen={showQuickAdd}
                onClose={() => setShowQuickAdd(false)}
                onSuccess={() => {
                    fetchData();
                }}
            />

            {/* Global Keyboard Shortcuts */}
            <KeyboardShortcuts onQuickAdd={() => setShowQuickAdd(true)} />
        </div>
    );
};

export default Layout;
